// src/components/FavouriteTeams.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaTrash } from "react-icons/fa";
import styles from "../css/FavouriteTeams.module.css";
import Footer from "./Footer";
import { baseUrl } from "../api/api";

const FavouriteTeams = () => {
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // 1. Load followed team IDs into state once
  const [teams, setTeams] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("followedTeams")) || [];
    } catch {
      return [];
    }
  });

  // 2. Data fetching state
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 3. Fetch favorites data on mount
  useEffect(() => {
    if (!teams.length) {
      setLoading(false);
      return;
    }
    axios
      .get(
     `${baseUrl}/api-football/team/matches?teams=${teams.join(
          ","
        )}`
      )
      .then((resp) => setData(resp.data))
      .catch((err) => setError(err))
      .finally(() => setLoading(false));
  }, []); // empty deps ensures this runs only once

  // 4. Remove from favorites handler
  const removeFavorite = (teamId) => {
    const updated = teams.filter((id) => id !== teamId);
    setTeams(updated);
    localStorage.setItem("followedTeams", JSON.stringify(updated));
  };

  // 5. Loading / error / empty states
  if (loading)
    return (
      <div className={styles.loadingWrapper}>
        <div className={styles.loader}></div>
      </div>
    );
  if (error) return <div className={styles.error}>Error: {error.message}</div>;
  if (!teams.length)
    return (
      <div className={styles.noTeams}>
        You have no favorite teams yet.
        <Footer />
      </div>
    );

  // 6. Filter and sort teams
  const filtered = data.teams.filter((td) => teams.includes(Number(td.teamId)));
  filtered.sort((a, b) => b.liveMatches.length - a.liveMatches.length);

  // 7. Helper: derive display name
  const deriveName = (arr, teamIdNum) => {
    if (!arr.length) return null;
    const m = arr[0];
    return m.teams.home.id === teamIdNum
      ? m.teams.home.name
      : m.teams.away.name;
  };

  // 8. Helper: render match lists
  const renderList = (matches, emptyText) =>
    matches.length ? (
      matches.map((m) => (
        <li key={m.fixture.id} className={styles.matchItem}>
          <div className={styles.matchDate}>
            {new Date(m.fixture.date).toLocaleString(undefined, {
              timeZone: userTimeZone,
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
          <div className={styles.matchScore}>
            {/* Home logo on left */}
            <span>
              <img
                src={m.teams.home.logo}
                alt={m.teams.home.name}
                className={styles.miniLogo}
              />
              {m.teams.home.name}
            </span>

            {/* Score */}
            <strong>
              {m.goals.home} - {m.goals.away}
            </strong>

            {/* Away logo now also on left */}
            <span>
              <img
                src={m.teams.away.logo}
                alt={m.teams.away.name}
                className={styles.miniLogo}
              />
              {m.teams.away.name}
            </span>
          </div>
        </li>
      ))
    ) : (
      <li className={styles.matchItemEmpty}>{emptyText}</li>
    );

  return (
    <div className={styles.container}>
      {filtered.map((teamData) => {
        const teamIdNum = Number(teamData.teamId);
        const teamName =
          deriveName(teamData.liveMatches, teamIdNum) ||
          deriveName(teamData.upcomingMatches, teamIdNum) ||
          deriveName(teamData.lastWeekResults, teamIdNum) ||
          `Team ${teamIdNum}`;

        return (
          <div key={teamIdNum} className={styles.card}>
            <div className={styles.cardHeader}>
              <h2 className={styles.teamNameHeader}>{teamName}</h2>
              <button
                className={styles.removeBtn}
                onClick={() => removeFavorite(teamIdNum)}
              >
                <FaTrash className={styles.removeIcon} />
                Remove
              </button>
            </div>

            <div className={styles.cardBody}>
              <div className={styles.section}>
                <h3>Live</h3>
                <ul className={styles.list}>
                  {renderList(teamData.liveMatches, "No live matches")}
                </ul>
              </div>
              <div className={styles.section}>
                <h3>Upcoming</h3>
                <ul className={styles.list}>
                  {renderList(teamData.upcomingMatches, "No upcoming matches")}
                </ul>
              </div>
              <div className={styles.section}>
                <h3>Recent Results</h3>
                <ul className={styles.list}>
                  {renderList(teamData.lastWeekResults, "No recent results")}
                </ul>
              </div>
            </div>
          </div>
        );
      })}
      <Footer />
    </div>
  );
};

export default FavouriteTeams;
